import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, KeyRound, Plus, Trash2, XCircle } from "lucide-react";
import { useState } from "react";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authed/llms/")({
  component: LlmsList,
});

function LlmsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin", "llms"],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/llms", { params: {} });
      if (error) throw new Error("failed to load llms");
      return data!;
    },
  });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const toggleActive = useMutation({
    mutationFn: async (vars: { id: string; is_active: boolean }) => {
      const { error } = await api.PATCH("/api/v1/admin/llms/{llm_id}", {
        params: { path: { llm_id: vars.id } },
        body: { is_active: vars.is_active },
      });
      if (error) throw new Error("toggle failed");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "llms"] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE("/api/v1/admin/llms/{llm_id}", {
        params: { path: { llm_id: id } },
      });
      if (error) throw new Error("delete failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "llms"] });
      setConfirmDelete(null);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">LLMs</h1>
          <p className="text-sm text-zinc-500">
            Provider credentials assignable to prompt versions. Active LLMs
            with a key on file are pickable in the version editor.
          </p>
        </div>
        <Button onClick={() => navigate({ to: "/llms/new" })}>
          <Plus className="mr-1 size-4" />
          New LLM
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {q.isPending ? (
            <p className="p-4 text-sm text-zinc-500">Loading…</p>
          ) : q.isError ? (
            <p className="p-4 text-sm text-red-600">Failed to load LLMs.</p>
          ) : q.data?.items.length === 0 ? (
            <p className="p-6 text-center text-sm text-zinc-500">
              No LLMs yet — create one to get started.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Code</TH>
                  <TH>Name</TH>
                  <TH>Provider · Model</TH>
                  <TH>Active</TH>
                  <TH>API key</TH>
                  <TH className="w-px text-right">Actions</TH>
                </TR>
              </THead>
              <TBody>
                {q.data!.items.map((l) => (
                  <TR key={l.id}>
                    <TD className="font-mono text-xs">
                      <Link
                        to="/llms/$id"
                        params={{ id: l.id }}
                        className="text-zinc-900 underline"
                      >
                        {l.code}
                      </Link>
                    </TD>
                    <TD className="text-sm">{l.name}</TD>
                    <TD className="text-xs text-zinc-600">
                      {l.provider} · {l.provider_model}
                    </TD>
                    <TD>
                      <Button
                        size="sm"
                        variant={l.is_active ? "secondary" : "ghost"}
                        onClick={() =>
                          toggleActive.mutate({ id: l.id, is_active: !l.is_active })
                        }
                        disabled={toggleActive.isPending}
                        className="gap-1"
                      >
                        {l.is_active ? (
                          <CheckCircle2 className="size-3.5 text-green-700" />
                        ) : (
                          <XCircle className="size-3.5 text-zinc-400" />
                        )}
                        {l.is_active ? "Active" : "Inactive"}
                      </Button>
                    </TD>
                    <TD>
                      <span className="inline-flex items-center gap-1 text-xs">
                        <KeyRound
                          className={
                            l.has_api_key
                              ? "size-3.5 text-green-700"
                              : "size-3.5 text-zinc-400"
                          }
                        />
                        {l.has_api_key ? "set" : "missing"}
                      </span>
                    </TD>
                    <TD className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelete(l.id)}
                        aria-label="Delete LLM"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {confirmDelete && (
        <Dialog
          open
          onClose={() => setConfirmDelete(null)}
          title="Delete LLM?"
        >
          <p className="text-sm text-zinc-700">
            Soft-delete this LLM. Existing usage history is preserved; the
            code becomes available for re-use.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(null)}
              disabled={remove.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => remove.mutate(confirmDelete)}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
